import { useState, useEffect } from 'react';
import type { User, UserInput } from '../../api';

interface Role {
  id: string;
  name: string;
}

interface UserFormProps {
  user: User | null;
  roles: Role[];
  onSubmit: (data: UserInput) => Promise<void> | void;
  onCancel: () => void;
  /** Show an Active checkbox and include is_active in the payload (local node). */
  showStatus?: boolean;
  passwordMinLength?: number;
  /** Optional helper text under the role select (e.g. local's group-access note). */
  roleNote?: string;
}

/**
 * Create/edit a user. Works on the canonical User shape (name/email/role_id/is_active, and how
 * alerts reach them: phone, notify_email, notify_message); each app's adapter maps to its backend
 * fields. Status (is_active) is opt-in. Routing policies reach a user on email and/or the Message API /
 * SMS by these.
 */
export default function UserForm({ user, roles, onSubmit, onCancel, showStatus = false, passwordMinLength = 8, roleNote }: UserFormProps) {
  const [form, setForm] = useState({
    name: '', email: '', password: '', role_id: '', is_active: true, phone: '', notify_email: true, notify_message: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role_id: user.role_id || '',
        is_active: user.is_active !== false,
        phone: user.phone || '',
        notify_email: user.notify_email !== false,
        notify_message: user.notify_message !== false,
      });
    }
  }, [user]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const data: UserInput = {
        name: form.name, email: form.email, role_id: form.role_id,
        phone: form.phone.trim(), notify_email: form.notify_email, notify_message: form.notify_message,
      };
      if (form.password) data.password = form.password;
      if (showStatus) data.is_active = form.is_active;
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  }

  const inputClass =
    'mt-1.5 w-full rounded-lg border border-border bg-input px-3 py-2 text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-[13px] font-medium text-foreground">Name *</label>
        <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
      </div>

      <div>
        <label className="block text-[13px] font-medium text-foreground">Email *</label>
        <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
      </div>

      <div>
        <label className="block text-[13px] font-medium text-foreground">
          Password {user ? '(leave blank to keep)' : '*'}
        </label>
        <input
          type="password"
          required={!user}
          minLength={passwordMinLength}
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder={`Minimum ${passwordMinLength} characters`}
          className={inputClass}
        />
      </div>

      <div>
        <label className="block text-[13px] font-medium text-foreground">Role *</label>
        <select required value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} className={inputClass}>
          <option value="">Select a role</option>
          {(roles || []).map((role) => (
            <option key={role.id} value={role.id}>{role.name?.replace(/_/g, ' ')}</option>
          ))}
        </select>
        {roleNote && <p className="mt-1 text-[11px] text-muted-foreground">{roleNote}</p>}
      </div>

      <fieldset className="rounded-lg border border-border px-3 pb-3 pt-1">
        <legend className="px-1 text-[12px] font-medium text-muted-foreground">Alert notifications</legend>
        <label className="block text-[13px] font-medium text-foreground">
          Phone (for Message API alerts)
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="919876543210 — with country code"
            className={inputClass}
          />
        </label>
        <div className="mt-2 flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-[13px] text-foreground">
            <input type="checkbox" checked={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.checked })} className="h-4 w-4 accent-brand" />
            Email alerts
          </label>
          <label className="flex items-center gap-2 text-[13px] text-foreground">
            <input type="checkbox" checked={form.notify_message} onChange={(e) => setForm({ ...form, notify_message: e.target.checked })} className="h-4 w-4 accent-brand" />
            Message API alerts
          </label>
        </div>
        <p className="mt-1.5 text-[11px] text-muted-foreground">Used when a routing policy names this person or their role — only for cameras they can see.</p>
      </fieldset>

      {showStatus && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="h-4 w-4 rounded border-border bg-input text-brand accent-brand"
          />
          <label htmlFor="is_active" className="text-[13px] text-foreground">Active</label>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={loading} className="rounded-lg bg-brand px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-dark disabled:opacity-50 transition-colors">
          {loading ? 'Saving…' : user ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
}
